import {Injectable} from '@angular/core';
import {AbstractService} from './abstract.service';
import {ApiVersion} from '../constants/apis';
import {User} from '../models/user.model';
import {Observable, Subject, Subscription} from 'rxjs';
import {switchMap} from 'rxjs/operators';
import {AuthService} from './auth.service';
import {DialogService} from './dialog.service';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {Router} from '@angular/router';
import {UserClassification} from '../constants/user-constants';
import {DomainFeature} from '../constants/domain-feature-constants';
import {Callback} from '../models/callback.model';
import {StorageService} from './storage.service';
import {environment} from '../../environments/environment';
import {extractErrorMessage} from '../utils/error-utils';


@Injectable({
  providedIn: 'root'
})
export class UserService extends AbstractService {

  private user$: User;
  public userSubject = new Subject<User>();
  private authSubscription: Subscription = new Subscription();

  constructor(private authService: AuthService,
              private router: Router,
              private http: HttpClient,
              private dialogService: DialogService,
              private storageService: StorageService) {
    super();
    this.authSubscription = this.authService.profileSubject.subscribe((evt) => {
      if (evt.profile) {
        this.getAodocsUser().pipe(
          switchMap(resp => {
            if (!this.storageService.getDomain()) {
              this.storageService.setDomain(resp.domain);
            }
            return this.getCurrentUser();
          })
        ).subscribe({
          next: async resp => {
            this.user = resp;
            await this.route(evt.callback);
          },
          error: error => this.dialogService.error(extractErrorMessage(error))
        });
      }
    });
  }

  getCurrentUser(): Observable<User> {
    const url = this.toFinraApiServiceUrl(ApiVersion.V1, 'user');
    const headers = new HttpHeaders().set('aodocs-domain', this.storageService.getDomain());
    return this.http.get<User>(url, {headers});
  }

  getAodocsUser(): Observable<{domain: string}> {
    const url = environment.aodocsUrl + 'api/user/v1/me';
    const headers = new HttpHeaders({Authorization: `Bearer ${this.authService.getToken()}`});
    return this.http.get<{domain: string}>(url, {headers});
  }

  set user(value: User) {
    this.user$ = value;
    this.userSubject.next(value);
  }

  get user(): User {
    return this.user$;
  }

  async route(callback: Callback): Promise<void> {
    if (callback) {
      this.router.navigateByUrl('/', {skipLocationChange: true}).then(() => {
        if (callback.params) {
          this.router.navigate([callback.url], {queryParams: callback.params});
        } else {
          this.router.navigateByUrl(callback.url);
        }
      });

    } else {
      if (this.isAuditor()) {
        await this.router.navigate(['audit/']);
      } else {
        await this.router.navigate(['libraries/']);
      }
    }
  }

  public isSuperAdmin(): boolean {
    if (this.user) {
      return (this.user.userClassification === UserClassification.AODOCS_SUPER_ADMIN)
        || (this.user.userClassification === UserClassification.RETENTION_ADMIN);
    } else {
      return false;
    }
  }

  public isAuditor(): boolean {
    return (this.user && this.user.userClassification === UserClassification.AUDITOR);
  }

  public isSimpleUser(): boolean {
    return (this.user && this.user.userClassification === UserClassification.USER);
  }

  public isFinra(): boolean {
    return this.isDomainFeature(DomainFeature.FINRA);
  }

  private isDomainFeature(feature: DomainFeature): boolean {
    if (!this.user.features) {
      return false;
    }
    return this.user.features.includes(feature);
  }
}
